import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import MetaHead from "./MetaHead";
import { useTranslation } from "react-i18next";
import { trackPageVisit } from "../utils/analytics";
import Footer from "./Footer";
import SocialHandles from "./SocialHandles";

const About: React.FC = () => {
  const { t } = useTranslation();

  useEffect(() => {
    
    trackPageVisit("about");
  }, []);

  return (
    <>
      <MetaHead
        title={t("about.title")}
        description={t("about.description") as string}
        keywords={t("about.keywords") as string}
      />
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-accent-50 dark:from-neutral-900 dark:via-neutral-800 dark:to-neutral-900 animate-fade-in">
        {/* Navbar */}
        <nav className="bg-white/80 dark:bg-neutral-900/80 backdrop-blur-sm border-b border-neutral-200 dark:border-neutral-700 sticky top-0 z-50">
          <div className="max-w-6xl mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              <Link to="/" className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center">
                  <svg
                    className="w-5 h-5 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 10V3L4 14h7v7l9-11h-7z"
                    />
                  </svg>
                </div>
                <span className="text-xl font-bold bg-gradient-to-r from-primary-600 to-accent-600 bg-clip-text text-transparent">
                  Streak Spark
                </span>
              </Link>
              <div className="flex items-center space-x-4">
                <Link
                  to="/"
                  className="text-neutral-600 dark:text-neutral-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors duration-300"
                >
                  {t("about.home")}
                </Link>
                <Link
                  to="/app"
                  className="bg-gradient-to-r from-primary-600 to-primary-700 text-white px-4 py-2 rounded-xl font-semibold hover:shadow-glow transition-all duration-300 hover:-translate-y-0.5"
                >
                  {t("about.openApp")}
                </Link>
              </div>
            </div>
          </div>
        </nav>

        <div className="max-w-4xl mx-auto px-4 py-8 text-center">
          {/* Hero Section */}
          <div className="mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl shadow-glow mb-6">
              <svg
                className="w-8 h-8 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 10V3L4 14h7v7l9-11h-7z"
                />
              </svg>
            </div>

            <h1 className="text-4xl md:text-6xl font-bold bg-gradient-to-r from-primary-600 via-accent-600 to-secondary-600 bg-clip-text text-transparent mb-4 leading-tight">
              {t("about.aboutTitle")}
            </h1>

            <p className="text-lg md:text-xl text-neutral-600 dark:text-neutral-300 mb-6 max-w-3xl mx-auto leading-relaxed">
              {t("about.aboutDescription")}
            </p>
          </div>

          {/* Content Section */}
          <div className="glass-card p-8 rounded-2xl mb-8">
            <h2 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100 mb-6">
              {t("about.ourMission")}
            </h2>
            <p className="text-neutral-600 dark:text-neutral-300 leading-relaxed mb-6">
              {t("about.missionText")}
            </p>

            <h2 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100 mb-6">
              {t("about.keyFeatures")}
            </h2>
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div className="text-left">
                <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-2">
                  {t("about.habitTracking")}
                </h3>
                <p className="text-sm text-neutral-600 dark:text-neutral-300">
                  {t("about.habitTrackingDesc")}
                </p>
              </div>
              <div className="text-left">
                <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-2">
                  {t("about.taskManagement")}
                </h3>
                <p className="text-sm text-neutral-600 dark:text-neutral-300">
                  {t("about.taskManagementDesc")}
                </p>
              </div>
              <div className="text-left">
                <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-2">
                  {t("about.progressVisualization")}
                </h3>
                <p className="text-sm text-neutral-600 dark:text-neutral-300">
                  {t("about.progressVisualizationDesc")}
                </p>
              </div>
              <div className="text-left">
                <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-2">
                  {t("about.dataSecurity")}
                </h3>
                <p className="text-sm text-neutral-600 dark:text-neutral-300">
                  {t("about.dataSecurityDesc")}
                </p>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100 mb-6">
              {t("about.developer")}
            </h2>
            <p
              className="text-neutral-600 dark:text-neutral-300 leading-relaxed mb-4"
              dangerouslySetInnerHTML={{
                __html: t("about.developerText") as string,
              }}
            />

            <h2 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100 mb-6">
              {t("about.contact")}
            </h2>
            <p
              className="text-neutral-600 dark:text-neutral-300 leading-relaxed"
              dangerouslySetInnerHTML={{
                __html: t("about.contactText") as string,
              }}
            />
          </div>

          {/* Upcoming Features Section */}
          <div className="glass-card p-8 rounded-2xl bg-gradient-to-br from-primary-50/50 to-accent-50/50 dark:from-primary-900/20 dark:to-accent-900/20 border-2 border-primary-200/50 dark:border-primary-800/50">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-secondary-500 to-accent-500 rounded-2xl shadow-glow mb-6">
                <svg
                  className="w-8 h-8 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                  />
                </svg>
              </div>

              <h2 className="text-2xl font-bold bg-gradient-to-r from-secondary-600 to-accent-600 bg-clip-text text-transparent mb-4">
                🚀 Exciting Updates Coming Soon!
              </h2>

              <p className="text-lg text-neutral-700 dark:text-neutral-300 mb-6 leading-relaxed">
                We're constantly working to make Streak Spark even better.
                Curious about what's next on our roadmap?
              </p>

              <div className="bg-white/60 dark:bg-neutral-800/60 rounded-xl p-6 mb-6 border border-neutral-200/50 dark:border-neutral-700/50">
                <p className="text-neutral-800 dark:text-neutral-200 font-medium mb-4">
                  🌟 Peek into our development pipeline and see the amazing
                  features we're crafting just for you!
                </p>

                <Link
                  to="/notes/n/upcoming-features-list-2dec57"
                  className="inline-flex items-center space-x-2 bg-gradient-to-r from-secondary-500 to-accent-500 text-white px-6 py-3 rounded-xl font-semibold hover:shadow-glow transition-all duration-300 hover:-translate-y-0.5"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  <span>Discover Upcoming Features</span>
                </Link>
              </div>

              <p className="text-sm text-neutral-600 dark:text-neutral-400">
                💡 Your feedback helps shape Streak Spark's future. Stay tuned
                for more!
              </p>
            </div>
          </div>
        </div>
        <SocialHandles />
        <Footer />
      </div>
    </>
  );
};

export default About;
